import UIStateContext from "contexts/UIStateContext";
import WorkOrderSearchContext from "contexts/WorkOrderSearchContext";
import useApplicationContext from "hooks/apollo/applicationContext/useApplicationContext";
import useGetWorkOrders from "hooks/apollo/workOrder/useGetWorkOrders";
import useSlideOutState from "hooks/state/useSlideOutState";
import debounce from "lodash/debounce";
import React, { useContext, useMemo } from "react";
import styled from "styled-components";
import SidebarView from "./SidebarView";
import SlideOutPanel from "./SlideOutPanel";

const SlideOutWrapper = styled.div`
  display: flex;
  flex-direction: row;
  grid-row: 1 / 3;
  max-height: 100%;
  overflow: hidden;
`;

function SidebarContainer() {
  const { workOrders, fetch: filterWorkOrders, fetching } = useGetWorkOrders();
  const slideOutState = useSlideOutState();
  const applicationContext = useApplicationContext();
  const {
    site: { customWorkOrderField1Label },
  } = applicationContext;

  const {
    numberOfFiltersApplied,
    setState: setFilters,
    resetState: clearFilters,
    state: { searchText, searchCategory },
  } = useContext(WorkOrderSearchContext);

  const {
    setState: setSidebarOpen,
    state: { sidebarOpen },
  } = useContext(UIStateContext);

  const handleClearFilters = () => {
    filterWorkOrders(clearFilters());
  };

  const handleChangeFilter = (filter) => {
    const filters = setFilters(filter);

    debouncedFilterWorkOrders(filters);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Rerendering/requerying on change defeats the point of debouncing
  const debouncedFilterWorkOrders = useMemo(() => {
    return debounce((filter) => {
      filterWorkOrders(filter);
    }, 500);
  }, []);

  const handleCategoryChange = (filter) => {
    setFilters(filter);

    handleChangeFilter(filter);
  };

  const toggleSidebar = () => {
    setSidebarOpen({ sidebarOpen: !sidebarOpen });
  };

  // @keep_search_fields_in_sync
  const searchCategoryOptions = [
    { label: "Work order code", value: "code" },
    { label: "Work order ID", value: "external_id" },
    { label: "Work order description", value: "description" },
    { label: "Work order status", value: "status" },
    { label: customWorkOrderField1Label, value: "custom_work_order_field" },
    { label: "Notes", value: "notes" },
    { label: "Item code", value: "item_code" },
    { label: "Item description", value: "item_description" },
    { label: "Customer", value: "customer" },
    { label: "Line type preference", value: "line_type_preference" },
    { label: "Item class", value: "item_class" },
    { label: "Item family", value: "item_family" },
    { label: "Item type", value: "item_type" },
    { label: "Item category", value: "item_category" },
    { label: "Service category", value: "service_category" },
  ].filter(Boolean);

  return (
    <SlideOutWrapper>
      <SlideOutPanel onClearFilters={handleClearFilters} />
      <SidebarView
        fetching={fetching}
        numberOfFiltersApplied={numberOfFiltersApplied}
        onCategoryChange={handleCategoryChange}
        onTextSearch={handleChangeFilter}
        onToggleSidebar={toggleSidebar}
        searchCategory={searchCategory}
        searchCategoryOptions={searchCategoryOptions}
        searchText={searchText}
        sidebarOpen={sidebarOpen}
        slideOutState={slideOutState}
        workOrders={workOrders}
      />
    </SlideOutWrapper>
  );
}

export default function SidebarContainerWrapper() {
  return <SidebarContainer />;
}
